import React, { ReactElement, useMemo } from 'react';
import { FormikField } from 'components/common/form/field/FormikField';
import i18n from 'i18n';
import styles from '../l1ObjectForm.module.scss';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { RtbCampaignListBasic } from 'core/rtbCampaign/RtbCampaign';
import {
  formatPriceWithCurrency,
  getCurrencyLabel
} from 'helper/CurrencyHelper';
import {
  L1ObjectManager,
  DefaultL1ObjectManager
} from 'core/l1Object/L1ObjectManager';
import { CustomField } from 'components/common/form/field/CustomField';

const defaultL1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager();
const bidStrategyFormatter = (value) =>
  value ? i18n.t(`l1Object.labels.bidStrategy.${value.toLowerCase()}`) : '';
export const RTBCampaignGroupForm: React.FC<{
  formType: string;
  isCBO: boolean;
  canEditBidStrategy: boolean;
  channel: string | undefined;
  currency: string;
  campaigns: RtbCampaignListBasic[];
  campaignBudgets: any[];
  objectiveOptions: SelectOptions[];
  canEditViewableImpressInReportSetting: boolean;
  onEditCampaignBudgetsBtn: () => void;
  getBudgetTips: () => ReactElement | string | undefined;
}> = ({
  isCBO,
  formType,
  channel,
  currency,
  campaigns,
  campaignBudgets,
  objectiveOptions,
  canEditBidStrategy,
  canEditViewableImpressInReportSetting,
  onEditCampaignBudgetsBtn,
  getBudgetTips
}) => {
  const bidStrategyOptions = useMemo(
    () => defaultL1ObjectManager.getL1ObjectBidStrategyOptions(channel),
    [channel]
  );
  const objectiveFormatter = (value) =>
    value ? i18n.t(`l1Object.labels.objective.${value.toLowerCase()}`) : '';
  const renderCampaignBudget = () => {
    return campaigns.map((campaign) => {
      const campaignBudget = campaignBudgets.find(
        (campaignBudget) =>
          campaign.id && campaignBudget.id.toString() === campaign.id.toString()
      );
      return (
        <div key={campaign.id}>
          {`${campaign.name}: ${formatPriceWithCurrency(currency, campaignBudget ? +campaignBudget.budget : 0)}`}
        </div>
      );
    });
  };
  return (
    <>
      <FormikField.InputGroup
        label={i18n.t('l1Object.labels.budget')}
        name='budget'
        type='number'
        prefix={getCurrencyLabel(currency)}
        hint={getBudgetTips()}
      />
      {campaignBudgets && (
        <CustomField name='campaignBudgets' label=''>
          <div className={styles.adSetBudgetContainer}>
            <div className={styles.adSetBudget}>{renderCampaignBudget()}</div>
            <IconWithTooltip
              icon={faPencilAlt}
              onClick={onEditCampaignBudgetsBtn}
              tooltipProps={{
                id: `adsetBudgetsEditTip`,
                tooltip: i18n.t('l1Object.hints.editRtbCampaignBudgets')
              }}
            />
          </div>
        </CustomField>
      )}
      {formType === 'create' ? (
        <FormikField.Select
          label={i18n.t('l1Object.labels.objectiveLabel')}
          name='objective'
          simpleValue
          options={objectiveOptions}
          className={styles.selectField}
        />
      ) : (
        <FormikField.Label
          label={i18n.t('l1Object.labels.objectiveLabel')}
          name='objective'
          formatter={objectiveFormatter}
        />
      )}
      {isCBO &&
        (canEditBidStrategy ? (
          <FormikField.Select
            label={i18n.t('l1Object.labels.bidStrategyLabel')}
            name='rtb.bid_strategy'
            simpleValue
            options={bidStrategyOptions}
            className={styles.selectField}
          />
        ) : (
          <FormikField.Label
            label={i18n.t('l1Object.labels.bidStrategyLabel')}
            name='rtb.bid_strategy'
            formatter={bidStrategyFormatter}
          />
        ))}
      {canEditViewableImpressInReportSetting && (
        <FormikField.Checkbox
          label={i18n.t('l1Object.labels.viewableImpressReport')}
          name='rtb.viewable_impress_report'
        />
      )}
    </>
  );
};
